import React, { useState } from "react";

import {
  faCircleNotch,
  faTriangleExclamation,
} from "@fortawesome/pro-regular-svg-icons";
import { twJoin } from "tailwind-merge";

import { ModelQuotasConfig, SimulatedQuotasResponse } from "src/aiNode/types";
import { Pill } from "src/base-components/Pill";
import { ModelProvider } from "src/clients/agent-management";
import { EmptyState } from "src/design-system/EmptyState";
import { Icon } from "src/design-system/Icon";
import { Skeleton } from "src/design-system/Skeleton";
import {
  PROVIDER_DISPLAY_NAMES,
  PROVIDER_ICON_ASSETS,
} from "src/genai/ModelSelector/ModelIcon";
import { formatCount, formatLimit } from "src/utils/agents/numbers";

type Props = {
  data: SimulatedQuotasResponse | undefined;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
};

const PROVIDER_ORDER: ModelProvider[] = [
  "openai",
  "bedrock",
  "google_vertex",
  // Not including Anthropic since it's only supported
  // with BYOK where we don't support quotas yet
];

const orderedProviders = (
  quotas: SimulatedQuotasResponse["quotas"],
): ModelProvider[] => {
  const present = new Set(Object.keys(quotas) as ModelProvider[]);
  return PROVIDER_ORDER.filter((p) => present.has(p));
};

export const SimulatedQuotasTable: React.FC<Props> = ({
  data,
  isLoading,
  isFetching,
  isError,
}) => {
  const providers = data ? orderedProviders(data.quotas) : [];
  const [activeProvider, setActiveProvider] = useState<ModelProvider | null>(
    null,
  );
  const selectedProvider: ModelProvider | null =
    activeProvider && providers.includes(activeProvider)
      ? activeProvider
      : (providers[0] ?? null);

  if (isError) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white">
        <EmptyState
          description="Failed to simulate quotas. Your changes can still be saved."
          headline="Couldn't load simulated quotas"
          icon={faTriangleExclamation}
        />
      </div>
    );
  }

  if (isLoading && !data) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <Skeleton height="h-8" width="w-64" />
        <div className="mt-4 space-y-2">
          <Skeleton height="h-10" width="w-full" />
          <Skeleton height="h-10" width="w-full" />
          <Skeleton height="h-10" width="w-full" />
        </div>
      </div>
    );
  }

  if (!data || providers.length === 0 || !selectedProvider) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white py-8 text-center font-inter-normal-13px text-gray-500">
        No simulated quotas available.
      </div>
    );
  }

  const models = data.quotas[selectedProvider] ?? {};
  const modelEntries = Object.entries(models) as [string, ModelQuotasConfig][];

  return (
    <div
      className={twJoin(
        "overflow-hidden rounded-xl border border-gray-200 bg-white transition-opacity",
        isFetching && "opacity-60",
      )}
    >
      <div
        className="flex items-center justify-between border-gray-200 border-b bg-gray-50 px-4 py-3 font-inter-medium-13px text-gray-700"
        data-loc="simulated-quotas-baseline-header"
      >
        <span>Workspace baseline</span>
        {isFetching && (
          <span
            className="flex items-center gap-2 font-inter-normal-12px text-gray-500"
            data-loc="simulated-quotas-loading"
          >
            <Icon icon={faCircleNotch} size="xs" spin />
            <span>Updating…</span>
          </span>
        )}
      </div>
      <div className="flex gap-4 border-gray-200 border-b bg-gray-50 px-4">
        {providers.map((provider) => {
          const isActive = provider === selectedProvider;
          const count = Object.keys(data.quotas[provider] ?? {}).length;
          return (
            <button
              key={provider}
              className={twJoin(
                "relative flex items-center gap-2 py-3 font-inter-medium-13px focus:outline-none",
                isActive
                  ? "text-gray-900"
                  : "text-gray-600 hover:text-gray-900",
                isActive &&
                  "after:absolute after:right-0 after:bottom-0 after:left-0 after:h-0.5 after:bg-indigo-500",
              )}
              data-loc={`simulated-quotas-tab-${provider}`}
              type="button"
              onClick={() => setActiveProvider(provider)}
            >
              <img
                alt=""
                aria-hidden="true"
                className="h-4 w-4"
                src={PROVIDER_ICON_ASSETS[provider]}
              />
              <span>{PROVIDER_DISPLAY_NAMES[provider]}</span>
              <Pill variant="gray">
                <Pill.Text>{count}</Pill.Text>
              </Pill>
            </button>
          );
        })}
      </div>

      <table className="w-full table-fixed" data-loc="simulated-quotas-table">
        <thead className="bg-white">
          <tr className="border-gray-100 border-b font-inter-medium-12px text-gray-500 uppercase tracking-wide">
            <th className="w-2/5 px-4 py-3 text-left">Model</th>
            <th className="px-4 py-3 text-right">Monthly tokens</th>
            <th className="px-4 py-3 text-right">TPM</th>
            <th className="px-4 py-3 text-right">RPM</th>
          </tr>
        </thead>
        <tbody>
          {modelEntries.length === 0 ? (
            <tr>
              <td
                className="px-4 py-6 text-center font-inter-normal-13px text-gray-500"
                colSpan={4}
              >
                No models configured for this provider.
              </td>
            </tr>
          ) : (
            modelEntries.map(([modelId, config]) => (
              <tr
                key={modelId}
                className="border-gray-100 border-b font-inter-normal-13px font-mono text-gray-800 last:border-b-0"
              >
                <td className="px-4 py-3">{modelId}</td>
                <td className="px-4 py-3 text-right">
                  {formatLimit(config.monthly_tokens)}
                </td>
                <td className="px-4 py-3 text-right">
                  {formatLimit(config.tpm)}
                </td>
                <td className="px-4 py-3 text-right">
                  {formatCount(config.rpm)}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};
